<template>
    <div id="app" :class="{ 'is-compact-layout': isCompactLayout }">
        <transition v-if="!$route.meta.NoNeedHome" name="fade" mode="out-in">

            <el-row class="container" :class="{ 'is-portal-home': isPortalHome }">
                <!--
                  全新顶栏（2026-07-26）：品牌区与侧栏同宽对齐 + 右侧操作条
                  结构： [Brand 210/64] | [☰ 标题 ............ 学校 头像]
                  门户首页全屏：隐藏顶栏，进入模块后再显示
                -->
                <el-col v-if="!isPortalHome" :span="24" class="app-topbar" :class="{
                    'is-sidebar-collapsed': collapsed,
                    'is-compact': isCompactLayout,
                    'is-no-sidebar': !isSidebarVisible
                }">
                    <!-- 顶栏左侧品牌/学校区：有 Logo 才显示；加载失败则隐藏。最后更新：2026-10-30 -->
                    <div class="app-topbar__brand" v-show="!isCompactLayout && isSidebarVisible">
                        <div
                            v-if="showHeaderLogo"
                            class="app-topbar__brand-logo-wrap"
                            title="点击返回首页"
                            @click="goHome"
                        >
                            <img
                                v-if="headerSchoolLogo"
                                :key="'brand-logo-' + selectedSchoolId + '-' + headerSchoolLogo"
                                class="app-topbar__brand-logo"
                                v-oss-src="{ src: headerSchoolLogo, schoolId: selectedSchoolId }"
                                alt=""
                                @error="onHeaderLogoError"
                            />
                            <img
                                v-else
                                class="app-topbar__brand-logo"
                                :src="defaultPlatformLogo"
                                alt=""
                                @error="onHeaderLogoError"
                            />
                        </div>
                        <div class="app-topbar__brand-main">
                            <span class="app-topbar__brand-text" :title="headerSchoolName + '（点击回首页，可划选复制）'"
                                @click="goHome">{{ headerSchoolName }}</span>
                            <button type="button" class="app-topbar__brand-copy" title="点击复制学校名称" aria-label="复制学校名称"
                                @click.stop="copySchoolName">
                                <i class="el-icon-document-copy" aria-hidden="true"></i>
                            </button>
                        </div>
                    </div>
                    <div class="app-topbar__bar">
                        <button
                            v-if="isSidebarVisible"
                            type="button"
                            class="app-topbar__toggle"
                            :class="{ 'is-open': isCompactLayout && !collapsed }"
                            @click="collapse"
                            :aria-label="collapsed ? '展开菜单' : '收起菜单'"
                        >
                            <span class="app-topbar__burger" aria-hidden="true">
                                <span></span>
                                <span></span>
                                <span></span>
                            </span>
                        </button>
                        <div class="app-topbar__title" v-if="currentPageTitle">{{ currentPageTitle }}</div>
                        <div class="app-topbar__spacer"></div>
                        <div class="app-topbar__actions">
                            <el-select v-if="schoolSelectOptions.length > 0" :key="schoolSelectKey"
                                v-model="selectedSchoolId" filterable size="small" placeholder="选择学校"
                                @change="handleSchoolChange" class="app-topbar__school">
                                <el-option v-for="item in schoolSelectOptions" :key="item.value" :label="item.label"
                                    :value="item.value" />
                            </el-select>
                            <el-dropdown :trigger="userDropdownTrigger" placement="bottom-end" :hide-on-click="true"
                                popper-append-to-body class="app-topbar__user">
                                <button type="button" class="app-topbar__user-btn" title="账户与布局">
                                    <span class="app-topbar__user-name" v-if="!isCompactLayout && sysUserName">{{
                                        sysUserName }}</span>
                                    <img class="app-topbar__avatar" src="./assets/logo.png" width="32" height="32" alt="" />
                                </button>
                                <el-dropdown-menu slot="dropdown" class="user-profile-dropdown">
                                    <div class="dropdown-segment-container" tabindex="-1">
                                        <div class="dropdown-segment-group">
                                            <div class="segment-title">界面布局</div>
                                            <div class="segment-options">
                                                <div class="segment-option"
                                                    :class="{ 'active': $store.state.sysLayoutMode === 'classic' }"
                                                    @click="changeLayout('classic')">
                                                    <i class="el-icon-s-fold"></i>
                                                    <span>经典</span>
                                                </div>
                                                <div class="segment-option"
                                                    :class="{ 'active': $store.state.sysLayoutMode === 'portal' }"
                                                    @click="changeLayout('portal')">
                                                    <i class="el-icon-menu"></i>
                                                    <span>门户</span>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                    <el-dropdown-item class="user-profile-dropdown__item"
                                        @click.native="openMobileBindingDialog" tabindex="-1">
                                        <i class="el-icon-mobile-phone"></i>
                                        <span>个人中心</span>
                                    </el-dropdown-item>
                                    <el-dropdown-item class="user-profile-dropdown__item dropdown-logout-item"
                                        @click.native="logout" tabindex="-1">
                                        <i class="el-icon-switch-button"></i>
                                        <span>安全退出</span>
                                    </el-dropdown-item>
                                </el-dropdown-menu>
                            </el-dropdown>
                        </div>
                    </div>
                </el-col>

                <el-col :span="24" class="main" :class="{
                    'is-compact': isCompactLayout,
                    'is-drawer-open': isCompactLayout && !collapsed,
                    'is-portal-home': isPortalHome
                }">
        <aside :class="collapsedClass" v-if="isSidebarVisible">
            <div class="aside-drawer-head" v-if="isCompactLayout && !collapsed">
                <span class="aside-drawer-head__title">导航菜单</span>
                <button type="button" class="aside-drawer-head__close" @click="collapse" aria-label="关闭菜单">
                    <i class="el-icon-close" aria-hidden="true"></i>
                </button>
            </div>
            <el-scrollbar ref="asideScrollbar" style="height:100%;background: #2f3e52;" class="scrollbar-handle">
                <div v-if="$store.state.sysLayoutMode === 'portal'" class="back-to-portal-btn" @click="toindex">
                    <i class="el-icon-back"></i>
                    <span>返回应用中心</span>
                </div>
                <!-- 侧栏菜单搜索：防抖过滤；搜索态用 CSS 展开，勿按关键字重挂 el-menu。更新于 2026-09-23 -->
                <div v-show="!(collapsed && !isCompactLayout)" class="aside-menu-search">
                    <el-input v-model="menuSearchKeyword" size="small" clearable prefix-icon="el-icon-search"
                        placeholder="搜索菜单…" @input="onMenuSearchInput"
                        @compositionstart.native="onMenuSearchCompositionStart"
                        @compositionend.native="onMenuSearchCompositionEnd" @clear="onMenuSearchClear" />
                </div>
                <el-menu v-if="!menuSearchApplied.trim() || filteredLeftSideMenus.length" :default-active="$route.path"
                    class="el-menu-vertical-demo" :class="{
                        'portal-menu-theme': $store.state.sysLayoutMode === 'portal',
                        'is-menu-searching': !!menuSearchApplied.trim()
                    }" @open="handleopen" @close="handleclose" @select="handleselect"
                    :unique-opened="!menuSearchApplied.trim()" router :collapse="collapsed && !isCompactLayout"
                    background-color="#2f3e52" style="border-right: none;" text-color="#fff"
                    :active-text-color="$store.state.sysLayoutMode === 'portal' ? '#ffd04b' : '#ffd04b'">
                    <sidebar v-for="(menu, index) in filteredLeftSideMenus"
                        :key="(menu && menu.id) || menu.path || index" :item="menu" />
                </el-menu>
                <div v-if="menuSearchApplied.trim() && !filteredLeftSideMenus.length" class="aside-menu-search-empty">
                    未找到匹配菜单
                </div>
            </el-scrollbar>
        </aside>

        <el-col :span="24" class="content-wrapper"
            :class="!isSidebarVisible ? 'content-full-width' : (collapsed ? 'content-collapsed' : 'content-expanded')">
            <div class="tags" v-if="showTags">

                <div id="tags-view-container" class="tags-view-container">
                    <scroll-pane ref="scrollPane" class="tags-view-wrapper">
                        <router-link v-for="(tag, index) in tagsList" ref="tag" :key="tag.path"
                            :class="{ 'active': isActive(tag.path) }"
                            :to="{ path: tag.path, query: tag.query, fullPath: tag.fullPath }" tag="span"
                            @click.middle.native="closeTags(index)" class="tags-view-item">
                            {{ tag.title }}
                            <span class="el-icon-close" @click.prevent.stop="closeTags(index)" />
                        </router-link>
                    </scroll-pane>

                </div>

                <!--<ul>-->
                <!--<li class="tags-li" v-for="(item,index) in tagsList" :class="{'active': isActive(item.path)}" :key="index">-->
                <!--<span class="tag-dot-inner"></span>-->
                <!--<router-link :to="item.path" class="tags-li-title">-->
                <!--{{item.title}}-->
                <!--</router-link>-->
                <!--<span class="tags-li-icon" @click="closeTags(index)"><i class="el-icon-close"></i></span>-->
                <!--</li>-->
                <!--</ul>-->

                <!-- 其他操作按钮 -->
                <div class="tags-close-box">
                    <el-dropdown @command="handleTags">
                        <el-button size="mini">
                            <i class="el-icon-arrow-down el-icon--right"></i>
                        </el-button>
                        <el-dropdown-menu size="small" slot="dropdown">
                            <el-dropdown-item command="other">关闭其他</el-dropdown-item>
                            <el-dropdown-item command="all">关闭所有</el-dropdown-item>
                        </el-dropdown-menu>
                    </el-dropdown>
                </div>
            </div>
            <transition name="fade" mode="out-in">
                <div class="content-az router-view-withly">
                    <!-- 含有母版的路由页面 -->
                    <router-view></router-view>
                </div>
            </transition>
        </el-col>

    </el-col>
    </el-row>

    </transition>

    <transition v-else name="fade" mode="out-in">

        <div>
            <router-view></router-view>
        </div>
    </transition>

    <el-dialog title="Unread Messages" :class="newsDialogCss" :visible.sync="NewsVisible" v-model="NewsVisible"
        :close-on-click-modal="false">
        <div>
            <el-tag v-for="tag in tagNews" :key="tag.name" closable class="tag-new" :type="tag.type">
                {{ tag.name }}
            </el-tag>

        </div>
    </el-dialog>

    <el-dialog :visible.sync="mobileBindingVisible" width="460px" custom-class="mobile-binding-dialog dialog-compact"
        :close-on-click-modal="false" append-to-body>
        <div slot="title" class="mbd-header">
            <div class="mbd-header__icon" aria-hidden="true">
                <i class="el-icon-mobile-phone"></i>
            </div>
            <div class="mbd-header__text">
                <div class="mbd-header__title">手机号绑定</div>
                <div class="mbd-header__sub">用于短信登录与账号安全校验</div>
            </div>
        </div>

        <div class="mbd-body">
            <div class="mbd-status" :class="mobileBindingStatus.IsBound ? 'is-bound' : 'is-empty'">
                <div class="mbd-status__meta">
                    <span class="mbd-status__label">当前绑定</span>
                    <span class="mbd-status__badge">{{ mobileBindingStatus.IsBound ? '已绑定' : '未绑定' }}</span>
                </div>
                <div class="mbd-status__value">
                    {{ mobileBindingStatus.IsBound ? (mobileBindingStatus.MaskedMobilePhone || '—') : '暂无绑定手机号' }}
                </div>
            </div>

            <div class="mbd-field">
                <label class="mbd-field__label" for="mbd-phone">手机号</label>
                <el-input id="mbd-phone" v-model="mobileBindingForm.MobilePhone" maxlength="11" clearable
                    placeholder="请输入 11 位手机号" @input="onMobileBindingPhoneInput">
                    <i slot="prefix" class="el-input__icon el-icon-phone-outline"></i>
                </el-input>
            </div>

            <div class="mbd-field">
                <label class="mbd-field__label" for="mbd-code">验证码</label>
                <div class="mbd-code-row">
                    <el-input id="mbd-code" v-model="mobileBindingForm.Code" maxlength="6" placeholder="6 位短信验证码">
                        <i slot="prefix" class="el-input__icon el-icon-message"></i>
                    </el-input>
                    <el-button class="mbd-code-btn" type="primary" plain :loading="mobileBindingSending"
                        :disabled="mobileBindingCountdown > 0" @click="sendMobileBindingCode">
                        {{ mobileBindingCountdown > 0 ? `${mobileBindingCountdown}s 后重发` : '获取验证码' }}
                    </el-button>
                </div>
            </div>

            <p class="mbd-tip" :class="{ 'is-unbind': mobileBindingIntent === 'unbind' }">
                <template v-if="mobileBindingIntent === 'unbind'">
                    解绑模式：请填写<strong>当前已绑定</strong>手机号，点击「获取验证码」后再确认解绑。
                </template>
                <template v-else-if="mobileBindingStatus.IsBound">
                    换绑请填写新手机号并获取验证码；若要解绑，先点「解绑」切换模式。
                </template>
                <template v-else>
                    绑定后可使用短信验证码登录后台，一个手机号仅可绑定一个账号。
                </template>
            </p>
        </div>

        <div slot="footer" class="mbd-footer">
            <el-button @click="mobileBindingVisible = false">取消</el-button>
            <el-button v-if="mobileBindingStatus.IsBound" type="danger" plain :loading="mobileBindingSaving"
                @click="unbindMobileBinding">解绑</el-button>
            <el-button type="primary" :loading="mobileBindingSaving" @click="saveMobileBinding">
                {{ mobileBindingStatus.IsBound ? '确认换绑' : '确认绑定' }}
            </el-button>
        </div>
    </el-dialog>

    <div class="app-sidebar-mask" @click="collapse" v-show="SidebarVisible" tabindex="0" aria-hidden="true"></div>
    </div>
</template>
<script>
import Sidebar from './components/Sidebar'
import ScrollPane from './components/ScrollPane'
import { getUserByToken } from './api/modules/auth';
import { getSchoolDepartmentTree, clearDeptTreeCache } from './api/modules/department';
import { deptIdsEqual, normalizeDeptId } from './utils/deptCascaderProps';
import { saveUserConfig } from './api/modules/misc';
import { getMobileBinding, sendMobileBindingCode, bindMobilePhone, changeMobilePhone, unbindMobilePhone } from './api/modules/system';
import { fetchAndApplyUserLayout } from './utils/layoutMode';

import applicationUserManager from "./Auth/applicationusermanager";
import userAuth from "./Auth/UserAuth";

export default {
    components: { Sidebar, ScrollPane },
    mixins: [userAuth],
    data() {
        return {
            sysName: '一柒吧管理平台',
            sysNameShort: 'BD',
            NewsVisible: false,
            collapsed: false,
            zModalShadow: false,
            SidebarVisible: false,
            collapsedClass: 'menu-expanded',
            sysUserName: '',
            newsDialogCss: 'news-dialog',
            sysUserAvatar: '',
            tagsList: [],
            schoolList: [],
            schoolSelectKey: 0,
            selectedSchoolId: null, // 当前选中的学校ID
            form: {
                name: '',
                region: '',
                date1: '',
                date2: '',
                delivery: false,
                type: [],
                resource: '',
                desc: ''
            },
            routes: [],
            tagNews: [
                { name: '前后端分离', type: '' },
                { name: 'vue.js', type: '' },
                { name: 'DDD领域驱动设计', type: 'success' },
                { name: '标签三', type: 'info' },
                { name: '欠费警告！', type: 'warning' },
                { name: '异常报告！', type: 'danger' }
            ],
            visible: false,
            top: 0,
            left: 0,
            selectedTag: {},
            affixTags: [],
            /** 窄屏紧凑布局（≤1200）：侧栏抽屉化、顶栏精简 */
            isCompactLayout: false,
            /** 侧栏菜单搜索输入框（即时）。创建于 2026-09-23 */
            menuSearchKeyword: '',
            /** 防抖后的生效关键字，驱动过滤。创建于 2026-09-23 */
            menuSearchApplied: ''
            ,
            mobileBindingVisible: false,
            // 与后端 DefaultContractResolver 一致，使用 PascalCase。创建于 2026-10-29
            mobileBindingStatus: { IsBound: false, MaskedMobilePhone: '' },
            mobileBindingForm: { MobilePhone: '', Code: '' },
            /** bind | change | unbind，影响验证码场景。创建于 2026-10-29 */
            mobileBindingIntent: 'bind',
            mobileBindingSending: false,
            mobileBindingSaving: false,
            mobileBindingCountdown: 0,
            mobileBindingTimer: null,
            /** 顶栏 Logo 加载失败后隐藏，避免裂图。创建于 2026-10-30 */
            headerLogoBroken: false,
            defaultPlatformLogo: require('./assets/logo.png')
        }
    },
    methods: {
        /** 点击左侧 Logo / 校名返回首页。创建于 2026-10-30 */
        goHome() {
            if (this.$route.path === '/') return;
            this.$router.push('/');
        },
        /** Logo 无法访问时隐藏，不展示裂图。创建于 2026-10-30 */
        onHeaderLogoError() {
            this.headerLogoBroken = true;
        },
        /** 快捷复制左上角学校名称。创建于 2026-10-30 */
        copySchoolName() {
            const text = (this.headerSchoolName || '').trim();
            if (!text) return;
            const onCopied = () => {
                this.$message({
                    message: `已复制学校名称：${text}`,
                    type: 'success',
                    duration: 2500,
                });
            };
            if (navigator.clipboard && navigator.clipboard.writeText) {
                navigator.clipboard.writeText(text).then(onCopied).catch(() => {
                    this.fallbackCopyText(text, onCopied);
                });
            } else {
                this.fallbackCopyText(text, onCopied);
            }
        },
        fallbackCopyText(text, callback) {
            try {
                const input = document.createElement('textarea');
                input.value = text;
                input.style.position = 'fixed';
                input.style.left = '-9999px';
                input.style.top = '-9999px';
                input.setAttribute('readonly', 'readonly');
                document.body.appendChild(input);
                input.select();
                const successful = document.execCommand('copy');
                document.body.removeChild(input);
                if (successful && callback) {
                    callback();
                } else if (successful) {
                    this.$message.success(`已复制学校名称：${text}`);
                } else {
                    this.$message.warning('请手动划选复制');
                }
            } catch (e) {
                this.$message.warning('请手动划选复制');
            }
        },
        /**
         * 规范化绑定状态（兼容偶发 camelCase）。创建于 2026-10-29
         */
        applyMobileBindingStatus(response) {
            const raw = response || {};
            const isBound = !!(raw.IsBound != null ? raw.IsBound : raw.isBound);
            const masked = raw.MaskedMobilePhone || raw.maskedMobilePhone || '';
            this.mobileBindingStatus = {
                IsBound: isBound,
                MaskedMobilePhone: masked
            };
            this.mobileBindingIntent = isBound ? 'change' : 'bind';
        },
        /** 打开个人中心并加载当前手机号绑定状态。创建于 2026-10-29 */
        openMobileBindingDialog() {
            this.mobileBindingVisible = true;
            this.mobileBindingForm = { MobilePhone: '', Code: '' };
            this.mobileBindingIntent = 'bind';
            getMobileBinding().then((data) => {
                if (data && data.success && data.response) {
                    this.applyMobileBindingStatus(data.response);
                }
            });
        },
        /** 手机号输入仅保留数字。创建于 2026-10-29 */
        onMobileBindingPhoneInput(value) {
            const phone = String(value || '').replace(/\D/g, '').slice(0, 11);
            this.mobileBindingForm.MobilePhone = phone;
        },
        /** 当前发码场景。创建于 2026-10-29 */
        resolveMobileBindingScene() {
            if (this.mobileBindingIntent === 'unbind') return 'AdminUnbindPhone';
            if (this.mobileBindingIntent === 'change' || this.mobileBindingStatus.IsBound) return 'AdminChangePhone';
            return 'AdminBindPhone';
        },
        /** 发送个人中心手机号验证码。最后更新：2026-10-29 */
        sendMobileBindingCode() {
            if (this.mobileBindingSending || this.mobileBindingCountdown > 0) return;
            if (!/^1[3-9]\d{9}$/.test(this.mobileBindingForm.MobilePhone)) {
                this.$message.warning('请输入合规的 11 位手机号');
                return;
            }
            this.mobileBindingSending = true;
            sendMobileBindingCode({
                MobilePhone: this.mobileBindingForm.MobilePhone,
                Scene: this.resolveMobileBindingScene()
            }).then((data) => {
                if (!data || !data.success) {
                    this.$message.error((data && data.msg) || '验证码发送失败');
                    return;
                }
                this.$message.success((data && data.msg) || '验证码已发送');
                this.mobileBindingCountdown = 60;
                if (this.mobileBindingTimer) clearInterval(this.mobileBindingTimer);
                this.mobileBindingTimer = setInterval(() => {
                    if (this.mobileBindingCountdown <= 1) {
                        clearInterval(this.mobileBindingTimer);
                        this.mobileBindingTimer = null;
                        this.mobileBindingCountdown = 0;
                    } else this.mobileBindingCountdown -= 1;
                }, 1000);
            }).finally(() => { this.mobileBindingSending = false; });
        },
        /** 保存个人中心手机号绑定或换绑。最后更新：2026-10-29 */
        saveMobileBinding() {
            this.mobileBindingIntent = this.mobileBindingStatus.IsBound ? 'change' : 'bind';
            if (!/^1[3-9]\d{9}$/.test(this.mobileBindingForm.MobilePhone)) {
                this.$message.warning('请输入合规的 11 位手机号');
                return;
            }
            if (!this.mobileBindingForm.Code) {
                this.$message.warning('请输入验证码');
                return;
            }
            this.mobileBindingSaving = true;
            const wasBound = this.mobileBindingStatus.IsBound;
            const action = wasBound ? changeMobilePhone : bindMobilePhone;
            action(this.mobileBindingForm).then((data) => {
                if (!data || !data.success) {
                    this.$message.error((data && data.msg) || '保存失败');
                    return;
                }
                const masked = (data.response && (data.response.mobilePhone || data.response.MobilePhone)) || '';
                this.applyMobileBindingStatus({
                    IsBound: true,
                    MaskedMobilePhone: masked
                });
                this.mobileBindingForm = { MobilePhone: '', Code: '' };
                this.$message.success(wasBound ? '换绑成功' : '绑定成功');
            }).finally(() => { this.mobileBindingSaving = false; });
        },
        /** 解绑当前账号手机号。最后更新：2026-10-29 */
        unbindMobileBinding() {
            this.mobileBindingIntent = 'unbind';
            if (!/^1[3-9]\d{9}$/.test(this.mobileBindingForm.MobilePhone) || !this.mobileBindingForm.Code) {
                this.$message.info('已切换到解绑模式，请填写当前绑定手机号并获取验证码');
                return;
            }
            this.$confirm('确定解绑当前手机号？解绑后将无法使用短信登录。', '解绑确认', {
                type: 'warning',
                confirmButtonText: '确认解绑',
                cancelButtonText: '取消'
            }).then(() => {
                this.mobileBindingSaving = true;
                unbindMobilePhone(this.mobileBindingForm).then((data) => {
                    if (!data || !data.success) {
                        this.$message.error((data && data.msg) || '解绑失败');
                        return;
                    }
                    this.applyMobileBindingStatus({ IsBound: false, MaskedMobilePhone: '' });
                    this.mobileBindingForm = { MobilePhone: '', Code: '' };
                    this.$message.success('解绑成功');
                }).finally(() => { this.mobileBindingSaving = false; });
            }).catch(() => { });
        },
        /** 中文输入法组字中，避免中间态触发过滤。创建于 2026-09-23 */
        onMenuSearchCompositionStart() {
            this._menuSearchComposing = true;
        },
        /** 组字结束再走防抖过滤。创建于 2026-09-23 */
        onMenuSearchCompositionEnd() {
            this._menuSearchComposing = false;
            this.onMenuSearchInput();
        },
        /** 输入防抖再过滤；搜索态不按关键字重挂菜单。更新于 2026-09-23 */
        onMenuSearchInput() {
            if (this._menuSearchComposing) return;
            if (this._menuSearchTimer) {
                clearTimeout(this._menuSearchTimer);
            }
            this._menuSearchTimer = setTimeout(() => {
                this.menuSearchApplied = this.menuSearchKeyword || '';
            }, 180);
        },
        /** 清空搜索立即还原。创建于 2026-09-23 */
        onMenuSearchClear() {
            if (this._menuSearchTimer) {
                clearTimeout(this._menuSearchTimer);
                this._menuSearchTimer = null;
            }
            this._menuSearchComposing = false;
            this.menuSearchKeyword = '';
            this.menuSearchApplied = '';
        },
        /**
         * 按名称递归过滤，保留树形多层；父级命中保留整枝，仅子级命中则保留路径。
         * 更新于 2026-09-23
         */
        filterMenusByKeyword(menus, keyword) {
            const kw = String(keyword || '').trim().toLowerCase();
            if (!kw) return menus || [];
            const walk = (list) => {
                const out = [];
                (list || []).forEach((node) => {
                    if (!node || node.IsButton || node.IsHide || node.hidden === true || node.hidden === 'true') {
                        return;
                    }
                    const nameHit = String(node.name || '').toLowerCase().indexOf(kw) !== -1;
                    const childHits = walk(node.children || []);
                    if (nameHit) {
                        out.push(node);
                    } else if (childHits.length) {
                        out.push(Object.assign({}, node, { children: childHits }));
                    }
                });
                return out;
            };
            return walk(menus);
        },
        /**
         * 将侧栏当前激活菜单滚到可视区域垂直中间，方便切 tab 后定位。
         * 创建于 2026-09-30；最后修改：2026-10-03 等子菜单展开/收起动画结束后再计算位置
         */
        scrollActiveAsideMenuToCenter() {
            if (!this.isSidebarVisible) return;
            if (this.collapsed && !this.isCompactLayout) return;

            const getRoot = () => {
                const scrollbar = this.$refs.asideScrollbar;
                return (scrollbar && scrollbar.$el) || (this.$el && this.$el.querySelector('aside'));
            };

            const tryScroll = () => {
                const root = getRoot();
                if (!root) return false;

                const active = root.querySelector('.el-menu-item.is-active');
                // 所在子菜单仍是 display:none 时量不到真实位置
                if (!active || !active.offsetParent) return false;

                const wrap = root.querySelector('.el-scrollbar__wrap');
                if (wrap) {
                    const wrapRect = wrap.getBoundingClientRect();
                    const itemRect = active.getBoundingClientRect();
                    const delta = (itemRect.top + itemRect.height / 2) - (wrapRect.top + wrapRect.height / 2);
                    if (Math.abs(delta) > 2) {
                        this.animateAsideMenuScroll(wrap, wrap.scrollTop + delta);
                    }
                    return true;
                }

                if (typeof active.scrollIntoView === 'function') {
                    active.scrollIntoView({ block: 'center', inline: 'nearest', behavior: 'smooth' });
                }
                return true;
            };

            // unique-opened 会同时收起旧子菜单、展开新子菜单（el-collapse-transition 约 300ms），
            // 过渡期间元素带 .collapse-transition，位置不准，需等其结束再滚动
            if (this._asideMenuWaitRaf) {
                cancelAnimationFrame(this._asideMenuWaitRaf);
                this._asideMenuWaitRaf = null;
            }
            const startAt = performance.now();
            const maxWait = 1000;
            const waitAndScroll = () => {
                const root = getRoot();
                const animating = !!(root && root.querySelector('.collapse-transition'));
                const timeout = performance.now() - startAt > maxWait;
                if ((!animating && tryScroll()) || timeout) {
                    this._asideMenuWaitRaf = null;
                    if (timeout) tryScroll();
                    return;
                }
                this._asideMenuWaitRaf = requestAnimationFrame(waitAndScroll);
            };
            waitAndScroll();
        },
        /**
         * 侧栏滚动条平滑动画（easeOutCubic）。创建于 2026-09-30
         * @param {HTMLElement} wrap - el-scrollbar 的滚动容器
         * @param {number} targetTop - 目标 scrollTop
         * @param {number} [duration=320] - 动画时长毫秒
         */
        animateAsideMenuScroll(wrap, targetTop, duration) {
            if (!wrap) return;
            const maxTop = Math.max(0, wrap.scrollHeight - wrap.clientHeight);
            const to = Math.max(0, Math.min(targetTop, maxTop));
            const from = wrap.scrollTop;
            const distance = to - from;
            if (Math.abs(distance) < 1) {
                wrap.scrollTop = to;
                return;
            }

            if (this._asideMenuScrollRaf) {
                cancelAnimationFrame(this._asideMenuScrollRaf);
                this._asideMenuScrollRaf = null;
            }

            const ms = duration > 0 ? duration : 320;
            const start = performance.now();
            const easeOutCubic = (t) => 1 - Math.pow(1 - t, 3);

            const step = (now) => {
                const progress = Math.min(1, (now - start) / ms);
                wrap.scrollTop = from + distance * easeOutCubic(progress);
                if (progress < 1) {
                    this._asideMenuScrollRaf = requestAnimationFrame(step);
                } else {
                    this._asideMenuScrollRaf = null;
                    wrap.scrollTop = to;
                }
            };
            this._asideMenuScrollRaf = requestAnimationFrame(step);
        },
        gotappath(Path) {
            console.log(Path);
            this.$router.replace({
                path: Path,
            });
        },
        handleOpen(key, keyPath) {
            console.log(key, keyPath);
        },
        toindex() {
            this.$router.replace({
                path: "/",
            });
        },
        handleClose(key, keyPath) {
            console.log(key, keyPath);
        },
        onSubmit() {
            console.log('submit!');
        },
        myNews() {

            this.newsDialogCss += ' show ';
            this.NewsVisible = true;
        },
        handleopen() {
            //console.log('handleopen');
        },
        handleclose() {
            //console.log('handleclose');
        },
        handleselect: function () {
            // 小屏抽屉菜单选中后自动收起，避免遮挡内容区
            if (this.isCompactLayout && !this.collapsed) {
                this.setCollapsed(true, { persist: false });
            }
        },
        //退出登录
        logout: function () {
            var _this = this;
            this.$confirm('确认退出吗?', '提示', {
                //type: 'warning'
            }).then(() => {
                window.localStorage.removeItem('user');
                window.localStorage.removeItem('Token');
                window.localStorage.removeItem('TokenExpire');
                window.localStorage.removeItem('NavigationBar');
                window.localStorage.removeItem('refreshtime');
                window.localStorage.removeItem('router');
                window.localStorage.removeItem('activeSchoolId');
                window.localStorage.removeItem('activeSchoolName');
                window.localStorage.removeItem('schoolList');
                sessionStorage.removeItem("Tags");

                clearDeptTreeCache();

                global.antRouter = [];

                this.tagsList = [];
                this.routes = [];
                this.schoolList = [];
                this.selectedSchoolId = null;
                this.$store.commit("saveTagsData", "");
                this.$store.commit("setActiveSchool", { schoolId: null, schoolName: '' });

                if (global.IS_IDS4) {
                    applicationUserManager.logout();
                } else {
                    _this.$router.push('/login');
                    //window.location.reload()
                }

            }).catch(() => {

            });


        },
        //设置
        Setting: function () {
            var _this = this;
            _this.$router.push('/System/My');
        },
        //源码
        goGithub() {
            window.open("https://github.com/anjoy8/Blog.Admin")
        },
        /**
         * 是否窄屏抽屉模式。用 matchMedia，避免仅靠 innerWidth；
         * 断点 768：仅手机/很窄窗口收起侧栏，浏览器轻微缩放不再误隐藏顶栏。
         */
        isMobileViewport() {
            if (window.matchMedia) {
                return window.matchMedia('(max-width: 768px)').matches;
            }
            return window.innerWidth <= 768;
        },
        /**
         * 设置侧栏折叠状态（非 toggle，避免初始化时状态翻转错误）。
         * @param {boolean} collapsed 是否收起
         * @param {{ persist?: boolean }} options persist=false 时不写 localStorage（用于小屏自适应）
         */
        setCollapsed(collapsed, options) {
            const persist = !options || options.persist !== false;
            this.collapsed = !!collapsed;
            if (this.collapsed) {
                this.collapsedClass = 'menu-collapsed';
                this.SidebarVisible = false;
            } else {
                this.collapsedClass = 'menu-expanded';
                // 小屏展开时显示遮罩，点击遮罩可收起
                this.SidebarVisible = this.isCompactLayout;
            }
            if (persist) {
                window.localStorage.collapse = String(this.collapsed);
            }
        },
        // 折叠导航栏（顶栏汉堡按钮 / 遮罩点击）
        collapse: function () {
            this.setCollapsed(!this.collapsed, { persist: !this.isCompactLayout });
        },
        /**
         * 同步窄屏标记，并在进入/离开小屏时处理侧栏。
         * 创建于 2026-07-26
         */
        syncCompactLayout() {
            const compact = this.isMobileViewport();
            const wasCompact = this.isCompactLayout;
            this.isCompactLayout = compact;

            if (compact && !wasCompact) {
                this.setCollapsed(true, { persist: false });
                return;
            }
            if (!compact && wasCompact) {
                this.setCollapsed(window.localStorage.collapse === 'true', { persist: false });
            }
        },
        // 动态一键无缝切换系统布局，同步保存至云端数据库
        changeLayout(mode) {
            const userStr = window.localStorage.getItem('user');
            const uId = userStr ? (JSON.parse(userStr).uID || 0) : 0;
            this.$store.commit('saveLayoutMode', mode);

            if (uId > 0) {
                saveUserConfig({
                    userId: uId,
                    configKey: 'layout',
                    configValue: mode
                }).then(res => {
                    if (res && res.data && res.data.success) {
                        this.$message.success('布局已同步保存至云端！');
                    }
                }).catch(err => {
                    console.error('云端保存布局失败，降级本地存储', err);
                });
            } else {
                this.$message.success('本地布局已更新！');
            }

            // 延迟重载，确保计算路由与侧边栏平滑更新
            setTimeout(() => {
                window.location.reload();
            }, 300);
        },
        /**
         * 已登录会话下从云端取回布局（刷新页面 / 换设备后本机 localStorage 过期时）
         * 若与本地不一致则写入并刷新一次，避免门户壳层半生效
         */
        syncLayoutFromCloud() {
            if (!window.localStorage.getItem('Token')) return;
            const userStr = window.localStorage.getItem('user');
            if (!userStr) return;
            let uId = 0;
            try {
                uId = JSON.parse(userStr).uID || 0;
            } catch (e) {
                return;
            }
            if (uId <= 0) return;

            fetchAndApplyUserLayout(uId, this.$store).then(({ changed }) => {
                if (changed) {
                    window.location.reload();
                }
            });
        },

        showMenu(i, status) {
            this.$refs.menuCollapsed.getElementsByClassName('submenu-hook-' + i)[0].style.display = status ? 'block' : 'none';
        },
        isActive(path) {
            return path === this.$route.fullPath;
        },
        // 关闭单个标签
        closeTags(index) {
            const delItem = this.tagsList.splice(index, 1)[0];
            const item = this.tagsList[index] ? this.tagsList[index] : this.tagsList[index - 1];
            if (item) {
                delItem.path === this.$route.fullPath && this.$router.push(item.path);

                this.$store.commit("saveTagsData", JSON.stringify(this.tagsList));
            } else {
                this.$router.push('/');
            }
        },
        // 关闭全部标签
        closeAll() {
            this.tagsList = [];
            this.$router.push('/');
            sessionStorage.removeItem("Tags");
        },
        // 关闭其他标签
        closeOther() {
            const curItem = this.tagsList.filter(item => {
                return item.path === this.$route.fullPath;
            })
            this.tagsList = curItem;


            sessionStorage.setItem("Tags", JSON.stringify(this.tagsList))
        },
        // 设置标签
        setTags(route) {
            if (!route.meta.NoTabPage) {
                const isExist = this.tagsList.some(item => {
                    return item.path === route.fullPath;
                })
                !isExist && this.tagsList.push({
                    title: route.meta.title,
                    path: route.fullPath,
                    // name: route.matched[1].components.default.name
                })
            }
        },
        // 当关闭所有页面时隐藏
        handleTags(command) {
            command === 'other' ? this.closeOther() : this.closeAll();
        },
        clearSchoolContext() {
            clearDeptTreeCache();
            this.schoolList = [];
            this.schoolSelectKey += 1;
            this.selectedSchoolId = null;
            this.$store.commit('setActiveSchool', { schoolId: null, schoolName: '' });
        },
        onUserLoggedIn() {
            this.loadRoutesFromStorage();
            this.loadSchoolList(true);
        },
        /**
         * 从 localStorage 恢复侧栏菜单树（登录后写入，刷新后需同步回 App）。
         * 创建于 2026-07-26
         */
        loadRoutesFromStorage() {
            if (this.routes && this.routes.length > 0) return;
            const raw = window.localStorage.getItem('router');
            if (!raw) return;
            try {
                const parsed = JSON.parse(raw);
                if (Array.isArray(parsed) && parsed.length > 0) {
                    this.routes = parsed;
                }
            } catch (e) {
                console.warn('恢复侧栏菜单失败', e);
            }
        },
        getUserInfoByToken(token) {
            var _this = this;
            var loginParams = { token: token };
            getUserByToken(loginParams).then(data => {
                this.logining = false;
                if (!data.success) {
                    _this.$message({
                        message: data.message,
                        type: 'error'
                    });
                } else {

                    _this.$notify({
                        type: "success",
                        message: `欢迎管理员：${data.response.uRealName} ！`,
                        duration: 3000
                    });

                    _this.sysUserName = data.response.uRealName;
                    window.localStorage.user = JSON.stringify(data.response)
                    // 登录获取成功后也自动刷新学校列表
                    this.loadSchoolList();
                }
            });
        },
        /**
         * 刷新后确保 Vuex Token 与 localStorage 一致，再请求学校列表。
         * 创建于 2026-07-26
         */
        ensureAuthFromStorage() {
            const token = window.localStorage.getItem('Token');
            const tokenExpire = window.localStorage.getItem('TokenExpire');
            if (token && token !== 'undefined' && !this.$store.state.token) {
                this.$store.commit('saveToken', token);
            }
            if (tokenExpire && !this.$store.state.tokenExpire) {
                this.$store.commit('saveTokenExpire', tokenExpire);
            }
        },
        loadSchoolList(forceReset = false) {
            if (forceReset) {
                clearDeptTreeCache();
                this.selectedSchoolId = null;
                this.$store.commit('setActiveSchool', { schoolId: null, schoolName: '' });
            }
            this.ensureAuthFromStorage();
            // 接口返回前先用本地活跃学校占位，避免顶栏学校下拉空白
            this.hydrateSelectedSchoolFromCache();
            let para = { pid: 0, type: 1 };
            getSchoolDepartmentTree(para).then(res => {
                // 兼容正常 axios 响应，以及拦截器失败时返回的 { success:false } 伪对象
                const treeData =
                    (res && res.data && res.data.response) ||
                    (res && res.response) ||
                    null;
                if (!treeData) {
                    console.warn('加载学校列表：无有效部门树', res);
                    return;
                }

                const schools = [];
                const traverse = (node) => {
                    if (!node) return;
                    if (Array.isArray(node)) {
                        node.forEach(child => traverse(child));
                        return;
                    }
                    const orgType = String(node.DeptOrgType || node.deptOrgType || '').toLowerCase();
                    if (orgType === 'school' || orgType === 'affiliateschool') {
                        const schoolId = normalizeDeptId(node.value)
                        if (!schoolId) return
                        schools.push({
                            value: schoolId,
                            label: node.label || node.name || node.Name,
                            logo: node.Logo || node.logo || ''
                        });
                    }
                    if (node.children && node.children.length) {
                        node.children.forEach(child => traverse(child));
                    }
                };
                traverse(treeData);

                // type=1 时若节点未标注 DeptOrgType，回退为根节点下直属学校
                if (schools.length === 0 && treeData.children && treeData.children.length) {
                    treeData.children.forEach(child => {
                        const schoolId = normalizeDeptId(child && child.value)
                        if (!schoolId) return;
                        schools.push({
                            value: schoolId,
                            label: child.label || child.name || child.Name,
                            logo: (child && (child.Logo || child.logo)) || ''
                        });
                    });
                }

                this.schoolList = schools;
                this.schoolSelectKey += 1;
                this.headerLogoBroken = false;

                if (this.schoolList.length > 0) {
                    const cachedId = forceReset
                        ? ''
                        : (normalizeDeptId(this.$store.state.activeSchoolId) || normalizeDeptId(window.localStorage.getItem('activeSchoolId')));
                    const match = cachedId ? this.schoolList.find(s => deptIdsEqual(s.value, cachedId)) : null;
                    if (match) {
                        this.selectedSchoolId = match.value;
                        this.$store.commit('setActiveSchool', {
                            schoolId: match.value,
                            schoolName: match.label
                        });
                    } else {
                        this.selectedSchoolId = this.schoolList[0].value;
                        this.$store.commit('setActiveSchool', {
                            schoolId: this.schoolList[0].value,
                            schoolName: this.schoolList[0].label
                        });
                    }
                    if (forceReset) {
                        this.$root.$emit('global-school-changed', this.selectedSchoolId);
                    }
                }
                // 列表为空时不清理本地活跃学校，保留顶栏/欢迎页校名
            }).catch(err => {
                console.error('加载学校列表失败', err);
            });
        },
        /**
         * 用 localStorage / Vuex 中的活跃学校恢复下拉选中值。
         * 创建于 2026-07-26
         */
        hydrateSelectedSchoolFromCache() {
            if (this.selectedSchoolId != null) return;
            const cachedId = normalizeDeptId(this.$store.state.activeSchoolId) || normalizeDeptId(window.localStorage.getItem('activeSchoolId'));
            if (cachedId) {
                this.selectedSchoolId = cachedId;
            }
        },
        handleSchoolChange(val) {
            this.headerLogoBroken = false;
            const match = this.schoolList.find(s => s.value === val);
            if (match) {
                this.$store.commit('setActiveSchool', {
                    schoolId: match.value,
                    schoolName: match.label
                });
                this.$root.$emit('global-school-changed', val);
            }
        },
    },
    computed: {
        /** 门户模式且在首页：全屏大桌面，隐藏顶栏/标签页 */
        isPortalHome() {
            const mode = this.$store.state.sysLayoutMode || 'classic';
            return mode === 'portal' && this.$route.path === '/';
        },
        showTags() {
            // 门户首页不显示标签页，进入模块后再显示
            if (this.isPortalHome) return false;
            return this.tagsList.length > 0;
        },
        /** 手机端 hover 会闪关，改用 click 才能点到退出。最后修改：2026-08-29 */
        userDropdownTrigger() {
            return this.isCompactLayout ? 'click' : 'hover';
        },
        // 动态计算Aside侧边栏需要渲染的路由树，支持双轨布局自适应过滤并清除空目录
        leftSideMenus() {
            const mode = this.$store.state.sysLayoutMode || 'classic';
            const isHome = this.$route.path === '/';

            // 辅助函数：深度递归检测当前节点及其子孙节点中，是否包含至少一个可见的跳转叶子路由，规避空壳目录
            const hasAnyVisibleLeaf = (node) => {
                if (node.IsButton || node.IsHide || node.hidden === true || node.hidden === 'true') {
                    return false;
                }
                // 拥有具体的有效跳转路径，说明是一个可见叶子路由
                if (node.path && node.path !== '-' && node.path !== ' ' && node.path !== '/') {
                    return true;
                }
                // 如果是目录，则递归探查其子路由是否含有至少一个可见的跳转叶子
                if (node.children && node.children.length > 0) {
                    return node.children.some(child => hasAnyVisibleLeaf(child));
                }
                return false;
            };

            // 1. 经典旧布局模式：直接渲染拥有至少一个可见页面的一级目录
            if (mode === 'classic') {
                return this.routes.filter(m => hasAnyVisibleLeaf(m));
            }

            // 2. 门户模式：首页无侧栏；进入大功能后，侧栏展示该分类下的全部一级菜单（可展开其子模块）
            if (mode === 'portal') {
                if (isHome) {
                    return [];
                }

                const currentPath = this.$route.path.toLowerCase();

                const hasPath = (node, targetPath) => {
                    if (node.path && node.path.toLowerCase() === targetPath) {
                        return true;
                    }
                    if (node.children && node.children.length > 0) {
                        for (let i = 0; i < node.children.length; i++) {
                            if (hasPath(node.children[i], targetPath)) {
                                return true;
                            }
                        }
                    }
                    return false;
                };

                // 当前路由所属的一级菜单
                const activeModule = this.routes.find(m => hasPath(m, currentPath));
                if (!activeModule) return [];

                const portalGroups = this.$store.state.portalDeptGroups || [];
                const activeId = activeModule.id != null ? String(activeModule.id) : '';

                // 优先用首页点击写入的大功能 key；否则按一级菜单 ID 反查所属分类
                let activeGroupKey = '';
                try {
                    activeGroupKey = window.sessionStorage.getItem('portal_active_group') || '';
                } catch (e) { /* ignore */ }

                let activeGroup = null;
                if (activeGroupKey && activeId) {
                    const cached = portalGroups.find(g => g && g.key === activeGroupKey && g.visible !== false) || null;
                    // 仅当当前一级菜单仍属于该大功能时沿用缓存，避免跨分类串侧栏
                    if (cached && (cached.menus || []).some(id => String(id) === activeId)) {
                        activeGroup = cached;
                    }
                }
                if (!activeGroup && activeId) {
                    activeGroup = portalGroups.find(g => {
                        if (!g || g.visible === false) return false;
                        return (g.menus || []).some(id => String(id) === activeId);
                    }) || null;
                }

                // 有大功能分类：侧栏按门户 menus 自定义顺序列出一级菜单
                if (activeGroup && Array.isArray(activeGroup.menus) && activeGroup.menus.length > 0) {
                    const routeMap = {};
                    this.routes.forEach(m => {
                        if (m && m.id != null) routeMap[String(m.id)] = m;
                    });
                    const groupMenus = [];
                    activeGroup.menus.forEach(id => {
                        const m = routeMap[String(id)];
                        if (!m) return;
                        if (!hasAnyVisibleLeaf(m)) return;
                        groupMenus.push(m);
                    });
                    if (groupMenus.length > 0) {
                        try {
                            window.sessionStorage.setItem('portal_active_group', activeGroup.key || '');
                        } catch (e) { /* ignore */ }
                        return groupMenus;
                    }
                }

                // 兜底：仅展示当前一级菜单的子模块
                return (activeModule.children || []).filter(c => hasAnyVisibleLeaf(c));
            }

            return this.routes.filter(m => hasAnyVisibleLeaf(m));
        },
        /** 侧栏按防抖关键字过滤后的菜单（保持树形）。更新于 2026-09-23 */
        filteredLeftSideMenus() {
            return this.filterMenusByKeyword(this.leftSideMenus, this.menuSearchApplied);
        },
        // 控制侧边栏在页面中的物理展示（门户大桌面首页隐藏，其余显示）
        isSidebarVisible() {
            const mode = this.$store.state.sysLayoutMode || 'classic';
            const isHome = this.$route.path === '/';
            return !(mode === 'portal' && isHome);
        },
        /** 顶栏校名 */
        headerSchoolName() {
            return this.$store.state.activeSchoolName || this.$store.state.DepartmentName || '管理平台';
        },
        /** 当前学校 Logo 路径（无则空，走平台默认图）。创建于 2026-10-30 */
        headerSchoolLogo() {
            const id = this.selectedSchoolId;
            if (id == null || !this.schoolList || !this.schoolList.length) return '';
            const match = this.schoolList.find(s => deptIdsEqual(s.value, id));
            const logo = match && (match.logo || match.Logo);
            return (logo && String(logo).trim()) || '';
        },
        /** 有可用 Logo 且未加载失败时才显示徽章。创建于 2026-10-30 */
        showHeaderLogo() {
            if (this.headerLogoBroken) return false;
            return !!(this.headerSchoolLogo || this.defaultPlatformLogo);
        },
        /**
         * 顶栏学校下拉选项：接口列表优先；刷新瞬间用本地活跃学校占位，避免空白。
         * 创建于 2026-07-26
         */
        schoolSelectOptions() {
            if (this.schoolList && this.schoolList.length > 0) {
                return this.schoolList;
            }
            const id = normalizeDeptId(this.$store.state.activeSchoolId) || normalizeDeptId(window.localStorage.getItem('activeSchoolId'));
            const name = this.$store.state.activeSchoolName || window.localStorage.getItem('activeSchoolName') || '';
            if (id && name) {
                return [{ value: id, label: name }];
            }
            return [];
        },
        /** 当前页标题：只取最后一级，避免 el-breadcrumb 换行叠字与漏出「/」 */
        currentPageTitle() {
            const matched = (this.$route.matched || []).filter((item) => !!item.name);
            if (!matched.length) return '';
            return matched[matched.length - 1].name;
        }
    },
    watch: {
        tagsList: {
            deep: true,
            handler(val) {
                if (val && val.length > 1) {
                    this.$store.commit("saveTagsData", JSON.stringify(val));
                }
            }
        },
        // 对router进行监听，每当访问router时，对tags的进行修改
        $route: async function (newValue, from) {
            const hasToken = window.localStorage.getItem('Token');
            const fromLogin = from && (from.path === '/login' || from.path === '/callback');
            // 登录成功已由 user-logged-in → loadSchoolList(true) 处理，此处跳过避免重复请求
            if (hasToken && !fromLogin && this.schoolList.length === 0) {
                this.loadSchoolList();
            }

            if (global.IS_IDS4) {
                await this.refreshUserInfo();
            }

            this.setTags(newValue);

            const tags = this.$refs.tag
            this.$nextTick(() => {
                if (tags) {
                    for (const tag of tags) {
                        if (tag.to.path === this.$route.path) {
                            this.$refs.scrollPane.moveToTarget(tag, tags)
                            // when query is different then update
                            // if (tag.to.fullPath !== this.$route.fullPath) {
                            //     this.$store.dispatch('tagsView/updateVisitedView', this.$route)
                            // }
                            break
                        }
                    }
                }
                // 切 tab 后侧栏虽已高亮，仍需滚到可视区中间
                this.scrollActiveAsideMenuToCenter();
            })

        },

    },
    created() {
        this.$root.$on('clear-school-context', this.clearSchoolContext);
        this.$root.$on('user-logged-in', this.onUserLoggedIn);
        // 刷新后尽早恢复菜单树，避免首屏侧栏空白
        this.loadRoutesFromStorage();
        // 先恢复本地活跃学校，顶栏下拉立刻有选项
        this.hydrateSelectedSchoolFromCache();
        // 第一次进入页面时，修改tag的值
        this.setTags(this.$route);
        // 小屏默认收起侧栏；大屏读取本地折叠偏好
        this.isCompactLayout = this.isMobileViewport();
        if (this.isCompactLayout) {
            this.setCollapsed(true, { persist: false });
        } else {
            this.setCollapsed(window.localStorage.collapse === 'true');
        }
    },
    mounted() {
        // 获取缓存部门名称
        var deptName = window.localStorage.getItem('DepartmentName');
        if (deptName) {
            this.$store.commit('setDepartmentName', deptName);
        }

        // 再次兜底恢复菜单（created 已加载一次；此处防止时序遗漏）
        this.loadRoutesFromStorage();

        if (window.localStorage.getItem('Token')) {
            this.ensureAuthFromStorage();
            this.loadSchoolList();
            // 刷新或带 Token 进入时，按登录用户取回云端布局偏好
            this.syncLayoutFromCloud();
        }

        var tags = sessionStorage.getItem('Tags') ? JSON.parse(sessionStorage.getItem('Tags')) : [];
        if (tags && tags.length > 0) {
            this.tagsList = tags;
        }

        // matchMedia + resize：缩放/改窗口都能同步侧栏抽屉状态
        if (window.matchMedia) {
            this._compactMql = window.matchMedia('(max-width: 768px)');
            this._onCompactMqlChange = () => this.syncCompactLayout();
            if (this._compactMql.addEventListener) {
                this._compactMql.addEventListener('change', this._onCompactMqlChange);
            } else if (this._compactMql.addListener) {
                this._compactMql.addListener(this._onCompactMqlChange);
            }
        }
        window.addEventListener('resize', this.syncCompactLayout);
        // 刷新进入深层级菜单时，首屏也将激活项滚到中间
        this.$nextTick(() => {
            this.scrollActiveAsideMenuToCenter();
        });
    },
    updated() {
        try {
            var user = JSON.parse(window.localStorage.user ? window.localStorage.user : null);
            if (user) {
                this.sysUserName = user.uRealName || '老张的哲学';
                this.sysUserAvatar = user.avatar || '../assets/user.png';
            }
        } catch (e) { /* ignore */ }

        const cachedId = normalizeDeptId(window.localStorage.getItem('activeSchoolId'));
        if (cachedId && this.selectedSchoolId === null && this.schoolList.length > 0) {
            const match = this.schoolList.find(s => deptIdsEqual(s.value, cachedId));
            if (match) this.selectedSchoolId = match.value;
        }

        this.loadRoutesFromStorage();
    },
    beforeDestroy() {
        if (this.mobileBindingTimer) {
            clearInterval(this.mobileBindingTimer);
            this.mobileBindingTimer = null;
        }
        if (this._menuSearchTimer) {
            clearTimeout(this._menuSearchTimer);
            this._menuSearchTimer = null;
        }
        if (this._asideMenuWaitRaf) {
            cancelAnimationFrame(this._asideMenuWaitRaf);
            this._asideMenuWaitRaf = null;
        }
        if (this._asideMenuScrollRaf) {
            cancelAnimationFrame(this._asideMenuScrollRaf);
            this._asideMenuScrollRaf = null;
        }
        window.removeEventListener('resize', this.syncCompactLayout);
        if (this._compactMql) {
            if (this._compactMql.removeEventListener) {
                this._compactMql.removeEventListener('change', this._onCompactMqlChange);
            } else if (this._compactMql.removeListener) {
                this._compactMql.removeListener(this._onCompactMqlChange);
            }
        }
        this.$root.$off('clear-school-context', this.clearSchoolContext);
        this.$root.$off('user-logged-in', this.onUserLoggedIn);
    },
}

</script>

<style lang="stylus">

</style>

<style lang="css">
@import "./style/home.css";
/*
 * 全站布局/手机适配（对齐 ezy-link-admin）：
 * 桌面内部滑、手机整页滑；分页样式见 pagination-override。
 * 须在 home.css 之后引入。最后修改：2026-08-24
 */
@import "./style/admin-responsive.css";
@import "./style/pagination-override.css";

.el-menu-vertical-demo {
    /*width: 230px;*/
}
</style>

<style>
.menu-collapsed .el-icon-arrow-right:before {
    display: none;
}

.tags {
    position: relative;
    overflow: hidden;
    border-bottom: 1px solid #e8edf3;
    background: #ffffff;
}

.tags ul {
    box-sizing: border-box;
    width: 100%;
    height: 100%;
    padding: 0;
    margin: 0;
    display: none;
}

.tags-li {
    float: left;
    margin: 3px 5px 2px 3px;
    border-radius: 3px;
    font-size: 13px;
    overflow: hidden;
    height: 23px;
    line-height: 23px;
    border: 1px solid #e9eaec;
    background: #fff;
    padding: 3px 5px 4px 12px;
    vertical-align: middle;
    color: #666;
    -webkit-transition: all .3s ease-in;
    transition: all .3s ease-in;
}

.tags-li-icon {
    cursor: pointer;
}

.tags-li:not(.active):hover {
    background: #f8f8f8;
}

.tags-li-title {
    float: left;
    max-width: 80px;
    overflow: hidden;
    white-space: nowrap;
    text-overflow: ellipsis;
    margin-right: 5px;
    color: #666;
    text-decoration: none;
}

.tags-li.active {
    /*color: #fff;*/
    /*border: 1px solid #10B9D3;*/
    /*background-color: #10B9D3;*/
}

.tags-li.active .tags-li-title {
    /*color: #fff;*/
}

.tags-close-box {
    box-sizing: border-box;
    text-align: center;
    z-index: 10;
    float: right;
    margin-right: 1px;
    line-height: 2;
}

::v-deep input[aria-hidden="true"] {
    display: none !important;
}

::v-deep .el-radio:focus:not(.is-focus):not(:active):not(.is-disabled) .el-radio__inner {
    box-shadow: none !important;
}
</style>
<style>
/*.logoban{*/
/*width: auto !important;*/
/*}*/
.news-dialog {

    background: #fff;
    z-index: 3000 !important;
    position: fixed;
    height: 100vh;
    width: 100%;
    max-width: 260px;
    top: 56px !important;
    left: 0 !important;
    -webkit-box-shadow: 0 0 15px 0 rgba(0, 0, 0, .05);
    box-shadow: 0 0 15px 0 rgba(0, 0, 0, .05);
    -webkit-transition: all .25s cubic-bezier(.7, .3, .1, 1);
    transition: all .25s cubic-bezier(.7, .3, .1, 1);
    -webkit-transform: translate(100%);
    z-index: 40000;
    left: auto !important;
    right: 0 !important;
    transform: translate(0);
}

.news-dialog .el-dialog {
    margin: auto !important;
    -webkit-box-shadow: 0 1px 3px rgba(0, 0, 0, .3);
    box-shadow: none;
    width: 100%;
}

.news-dialog.show {
    transform: translate(0);
}

.tag-new {
    width: 100%;
    margin: 10px 0;
}

@media screen and (max-width: 768px) {
    .mobile-ex {
        background: #fff;
        z-index: 3000;
        position: fixed;
        height: 100vh;
        width: 100%;
        max-width: 260px;
        top: 0;
        left: 0;
        -webkit-box-shadow: 0 0 15px 0 rgba(0, 0, 0, .05);
        box-shadow: 0 0 15px 0 rgba(0, 0, 0, .05);
        -webkit-transition: all .25s cubic-bezier(.7, .3, .1, 1);
        transition: all .25s cubic-bezier(.7, .3, .1, 1);
        -webkit-transform: translate(100%);
        z-index: 40000;
        left: auto;
        right: 0;
        transform: translate(100%);
    }

    .mobile-ex.menu-collapsed-mobile {
        transform: translate(0);
    }

    .el-menu--collapse {
        width: 100% !important;
    }

    .login-container {
        width: 300px !important;
    }

    .content-wrapper .tags {
        margin: 0;
        padding: 0;
    }

    .activeuser {
        display: none !important;
    }

    /* 内容区窄屏：整页 body 滚动，见 admin-responsive.css */
    #app.is-compact-layout .content-az,
    #app.is-compact-layout .router-view-withly {
        overflow: visible !important;
        max-width: 100% !important;
        box-sizing: border-box !important;
    }

    .tags-view-container {
        width: calc(100% - 48px) !important;
    }
}
</style>

<style>
/* tags 对齐 primary plain 按钮。最后修改：2026-08-29 */
.tags-view-container {
    height: 36px;
    width: calc(100% - 60px);
    float: left;
}

.tags-view-container .tags-view-wrapper .tags-view-item {
    display: inline-block;
    position: relative;
    cursor: pointer;
    height: 28px;
    line-height: 26px;
    border: 1px solid #e8edf3;
    border-radius: var(--btn-radius, 8px);
    color: #64748b;
    background: #fff;
    padding: 0 10px;
    font-size: 13px;
    font-weight: 500;
    margin-left: 6px;
    margin-top: 4px;
    transition: background-color 0.18s ease, border-color 0.18s ease, color 0.18s ease;
}

.tags-view-container .tags-view-wrapper .tags-view-item:first-of-type {
    margin-left: 12px;
}

.tags-view-container .tags-view-wrapper .tags-view-item:last-of-type {
    margin-right: 12px;
}

.tags-view-container .tags-view-wrapper .tags-view-item:hover:not(.active) {
    background: #f1f5f9;
    color: #334155;
    border-color: #e8edf3;
}

.tags-view-container .tags-view-wrapper .tags-view-item.active {
    background: var(--btn-primary-plain-bg, #eff6ff);
    color: var(--btn-primary, #2563eb);
    border-color: var(--btn-primary-plain-border, #bfdbfe);
}

.tags-view-container .contextmenu {
    margin: 0;
    background: #fff;
    z-index: 3000;
    position: absolute;
    list-style-type: none;
    padding: 5px 0;
    border-radius: var(--btn-radius, 8px);
    font-size: 12px;
    font-weight: 400;
    color: #333;
    border: 1px solid #e8edf3;
    box-shadow: 0 10px 25px rgba(0, 0, 0, 0.08);
}

.tags-view-container .contextmenu li {
    margin: 0;
    padding: 7px 16px;
    cursor: pointer;
}

.tags-view-container .contextmenu li:hover {
    background: #f1f5f9;
}
</style>

<style>
.tags-view-wrapper .tags-view-item .el-icon-close {
    width: 16px;
    height: 16px;
    vertical-align: 2px;
    border-radius: 50%;
    text-align: center;
    transition: background-color 0.18s ease, color 0.18s ease;
    transform-origin: 100% 50%;
}

.tags-view-wrapper .tags-view-item .el-icon-close:before {
    transform: scale(0.6);
    display: inline-block;
    vertical-align: -3px;
}

.tags-view-wrapper .tags-view-item .el-icon-close:hover {
    background-color: var(--btn-danger-plain-bg, #fef2f2);
    color: var(--btn-danger, #dc2626);
}

/* 侧边栏返回应用门户按钮：重塑为带有科技感发光与悬浮交互的精致蔚蓝胶囊 */
.aside-menu-search {
    padding: 10px 12px 6px;
    background: #2f3e52;
    position: sticky;
    top: 0;
    z-index: 2;
}

.aside-menu-search .el-input__inner {
    background: rgba(15, 23, 42, 0.45);
    border-color: rgba(148, 163, 184, 0.35);
    color: #e2e8f0;
    border-radius: 6px;
}

.aside-menu-search .el-input__inner::placeholder {
    color: #94a3b8;
}

.aside-menu-search .el-input__inner:focus {
    border-color: #60a5fa;
}

.aside-menu-search .el-input__prefix,
.aside-menu-search .el-input__suffix {
    color: #94a3b8;
}

.aside-menu-search-empty {
    padding: 24px 16px;
    text-align: center;
    color: #94a3b8;
    font-size: 13px;
}

/* 搜索态强制展开子菜单，避免依赖 default-openeds + :key 整树重挂。更新于 2026-09-23 */
.el-menu.is-menu-searching .el-submenu>.el-menu {
    display: block !important;
    height: auto !important;
    overflow: visible !important;
}

.el-menu.is-menu-searching .el-submenu__icon-arrow {
    display: none;
}

.back-to-portal-btn {
    margin: 12px 14px;
    padding: 10px 16px;
    color: rgba(255, 255, 255, 0.95);
    cursor: pointer;
    background-image: linear-gradient(135deg, #3b82f6 0%, #1d4ed8 100%);
    border-radius: 8px;
    font-size: 13px;
    font-weight: 600;
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 8px;
    box-shadow: 0 4px 12px rgba(29, 78, 216, 0.3);
    transition: all 0.25s cubic-bezier(0.4, 0, 0.2, 1);
}

.back-to-portal-btn:hover {
    transform: translateY(-2px);
    box-shadow: 0 6px 16px rgba(29, 78, 216, 0.45);
    color: #ffffff;
}

.back-to-portal-btn:active {
    transform: translateY(0);
}

/* ================== 用户头像下拉菜单高级感重塑 ================== */
.user-profile-dropdown {
    padding: 8px 4px !important;
    width: 200px !important;
    border-radius: 8px !important;
    border: 1px solid #e2e8f0 !important;
    background-color: #ffffff !important;
    box-shadow: 0 10px 25px rgba(0, 0, 0, 0.08) !important;
    z-index: 2200 !important;
}

/* 消除外层容器被聚焦时的 outline 虚线框 */
.dropdown-segment-container {
    outline: none !important;
}

.dropdown-segment-group {
    padding: 8px 12px 6px 12px;
}

.segment-title {
    font-size: 11px;
    font-weight: 600;
    color: #909399;
    margin-bottom: 6px;
    text-transform: uppercase;
    letter-spacing: 0.5px;
}

.segment-options {
    display: flex;
    background-color: #f1f5f9;
    /* 浅色模式下灰色背景 */
    padding: 2px;
    border-radius: 6px;
    gap: 2px;
}

.segment-option {
    flex: 1;
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 6px;
    font-size: 12px;
    padding: 8px 0;
    color: #64748b;
    cursor: pointer;
    border-radius: 4px;
    font-weight: 500;
    transition: all 0.2s ease;
    -webkit-tap-highlight-color: transparent;
    touch-action: manipulation;
}

.segment-option.active {
    background-color: #ffffff;
    color: #2f3e52;
    font-weight: 600;
    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.04);
}

.segment-option:not(.active):hover {
    color: #0f172a;
}

/* 个人中心 / 安全退出：统一行高，避免两项高低不一。最后更新：2026-10-30 */
.user-profile-dropdown__item {
    display: flex !important;
    align-items: center;
    gap: 6px;
    height: 40px !important;
    margin: 0 4px !important;
    padding: 0 12px !important;
    line-height: 1 !important;
    font-size: 13px !important;
    border-radius: 6px;
    box-sizing: border-box;
    transition: background-color 0.2s ease, color 0.2s ease;
    -webkit-tap-highlight-color: transparent;
    touch-action: manipulation;
}

.user-profile-dropdown__item i {
    font-size: 14px;
    width: 16px;
    text-align: center;
}

.dropdown-logout-item {
    color: #ef4444 !important;
    background-color: transparent !important;
    border-top: 1px solid #f1f5f9 !important;
    margin-top: 4px !important;
    border-radius: 0 0 6px 6px;
}

.dropdown-logout-item:hover {
    background-color: #fef2f2 !important;
}

.dropdown-logout-item:focus {
    background-color: transparent !important;
}

/* 侧栏物理隐藏时，大桌面门户主视图撑满 100% 宽度，消除右侧空白 */
.content-full-width {
    width: 100% !important;
}

/* ================== 当侧栏折叠 (Collapsed) 时，“返回应用中心”按钮的自适应极简圆形小球重塑 ================== */
.menu-collapsed .back-to-portal-btn {
    margin: 12px auto !important;
    /* 保证在 60px 宽度 aside 内部水平居中 */
    width: 36px !important;
    height: 36px !important;
    padding: 0 !important;
    /* 清空所有的 padding，保证正圆 */
    border-radius: 50% !important;
    /* 正圆形胶囊 */
    display: flex !important;
    align-items: center !important;
    justify-content: center !important;
    box-shadow: 0 4px 10px rgba(29, 78, 216, 0.3) !important;
}

/* 折叠状态下隐藏文字 span，只保留图标显示 */
.menu-collapsed .back-to-portal-btn span {
    display: none !important;
}

/* 折叠状态下图标居中且微调大小 */
.menu-collapsed .back-to-portal-btn i {
    font-size: 16px !important;
    margin: 0 !important;
}

/* ================== 当侧栏折叠 (Collapsed) 时，隐藏星标收藏图标，避免与默认主图标挤在一起重叠 ================== */
.menu-collapsed .star-icon-wrapper,
.el-menu--collapse .star-icon-wrapper {
    display: none !important;
}

/* 门户模式侧栏：与经典布局一致的扁平菜单，去掉卡片圆角/阴影感 */
.portal-menu-theme.el-menu {
    background-color: #2f3e52 !important;
}

.portal-menu-theme .el-menu-item,
.portal-menu-theme .el-submenu__title {
    border-radius: 0;
    margin: 0;
    width: 100%;
    box-shadow: none !important;
    text-shadow: none !important;
    filter: none !important;
    transition: background-color 0.2s ease, color 0.2s ease;
}

.portal-menu-theme .el-menu-item:hover,
.portal-menu-theme .el-submenu__title:hover {
    background-color: rgba(255, 255, 255, 0.08) !important;
}

.portal-menu-theme .el-menu-item.is-active {
    background: rgba(255, 255, 255, 0.1) !important;
    color: #ffd04b !important;
    box-shadow: none !important;
}

.portal-menu-theme .el-menu-item.is-active i,
.portal-menu-theme .el-menu-item.is-active .title-name {
    color: #ffd04b !important;
    text-shadow: none !important;
}

.portal-menu-theme .el-submenu.is-active>.el-submenu__title,
.portal-menu-theme .el-submenu.is-active>.el-submenu__title i,
.portal-menu-theme .el-submenu.is-active>.el-submenu__title .title-name {
    color: #ffd04b !important;
    text-shadow: none !important;
}

.portal-menu-theme .menu-fa-icon,
.portal-menu-theme .title-name,
.portal-menu-theme i {
    text-shadow: none !important;
    filter: none !important;
}

/* ========== 个人中心 · 手机号绑定弹窗。创建于 2026-10-29 ========== */
.mobile-binding-dialog.el-dialog {
    border-radius: 14px;
    border: 1px solid #e2e8f0;
    box-shadow: 0 24px 56px rgba(15, 23, 42, 0.16);
    overflow: hidden;
}

.mobile-binding-dialog .el-dialog__header {
    padding: 18px 20px 12px;
    border-bottom: none;
}

.mobile-binding-dialog .el-dialog__headerbtn {
    top: 18px;
    right: 18px;
}

.mobile-binding-dialog .el-dialog__body {
    padding: 4px 20px 8px;
}

.mobile-binding-dialog .el-dialog__footer {
    padding: 12px 20px 18px;
    border-top: 1px solid #f1f5f9;
}

.mbd-header {
    display: flex;
    align-items: center;
    gap: 12px;
    padding-right: 28px;
}

.mbd-header__icon {
    width: 40px;
    height: 40px;
    border-radius: 12px;
    display: flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
    color: #1d4ed8;
    background: linear-gradient(145deg, #eff6ff 0%, #dbeafe 100%);
    box-shadow: inset 0 0 0 1px rgba(37, 99, 235, 0.12);
}

.mbd-header__icon i {
    font-size: 20px;
}

.mbd-header__title {
    font-size: 16px;
    font-weight: 650;
    color: #0f172a;
    line-height: 1.3;
    letter-spacing: 0.01em;
}

.mbd-header__sub {
    margin-top: 2px;
    font-size: 12px;
    color: #64748b;
    line-height: 1.4;
}

.mbd-body {
    display: flex;
    flex-direction: column;
    gap: 14px;
}

.mbd-status {
    border-radius: 12px;
    padding: 12px 14px;
    border: 1px solid transparent;
    transition: background-color 0.2s ease, border-color 0.2s ease;
}

.mbd-status.is-empty {
    background: #f8fafc;
    border-color: #e2e8f0;
}

.mbd-status.is-bound {
    background: linear-gradient(135deg, #ecfdf5 0%, #f0fdf4 100%);
    border-color: #bbf7d0;
}

.mbd-status__meta {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 8px;
    margin-bottom: 6px;
}

.mbd-status__label {
    font-size: 12px;
    color: #64748b;
    font-weight: 500;
}

.mbd-status__badge {
    display: inline-flex;
    align-items: center;
    height: 22px;
    padding: 0 8px;
    border-radius: 999px;
    font-size: 12px;
    font-weight: 600;
    line-height: 1;
}

.mbd-status.is-empty .mbd-status__badge {
    color: #64748b;
    background: #e2e8f0;
}

.mbd-status.is-bound .mbd-status__badge {
    color: #15803d;
    background: rgba(34, 197, 94, 0.14);
}

.mbd-status__value {
    font-size: 18px;
    font-weight: 650;
    color: #0f172a;
    letter-spacing: 0.04em;
    font-variant-numeric: tabular-nums;
}

.mbd-status.is-empty .mbd-status__value {
    font-size: 14px;
    font-weight: 500;
    color: #64748b;
    letter-spacing: 0;
}

.mbd-field {
    display: flex;
    flex-direction: column;
    gap: 6px;
}

.mbd-field__label {
    font-size: 13px;
    font-weight: 600;
    color: #334155;
}

.mbd-code-row {
    display: flex;
    align-items: stretch;
    gap: 10px;
}

.mbd-code-row .el-input {
    flex: 1;
    min-width: 0;
}

.mbd-code-btn {
    flex-shrink: 0;
    min-width: 118px;
    padding-left: 12px;
    padding-right: 12px;
}

.mobile-binding-dialog .el-input__inner {
    height: 40px;
    line-height: 40px;
    border-radius: 10px;
    border-color: #dbe3ee;
    transition: border-color 0.2s ease, box-shadow 0.2s ease;
}

.mobile-binding-dialog .el-input__inner:focus {
    border-color: #3b82f6;
    box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.12);
}

.mobile-binding-dialog .el-input__prefix {
    left: 6px;
}

.mobile-binding-dialog .el-input--prefix .el-input__inner {
    padding-left: 34px;
}

.mbd-tip {
    margin: 0;
    padding: 10px 12px;
    border-radius: 10px;
    background: #f8fafc;
    border: 1px solid #eef2f7;
    color: #64748b;
    font-size: 12px;
    line-height: 1.55;
}

.mbd-tip.is-unbind {
    background: #fff7ed;
    border-color: #fed7aa;
    color: #9a3412;
}

.mbd-tip strong {
    font-weight: 650;
    color: inherit;
}

.mbd-footer {
    display: flex;
    justify-content: flex-end;
    flex-wrap: wrap;
    gap: 8px;
}

.mbd-footer .el-button {
    min-width: 84px;
    border-radius: 8px;
}

@media (max-width: 520px) {
    .mobile-binding-dialog {
        width: calc(100vw - 24px) !important;
    }

    .mbd-code-row {
        flex-direction: column;
    }

    .mbd-code-btn {
        width: 100%;
        min-width: 0;
    }
}

@media (prefers-reduced-motion: reduce) {

    .mbd-status,
    .mobile-binding-dialog .el-input__inner {
        transition: none;
    }
}
</style>
